import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, ReferenceLine } from 'recharts';
import { ChevronRight } from 'lucide-react';
import LABELS from './fieldLabels.json';
import { Card, SectionTitle, RatingBadge, Flag } from '../ui';
import { money, fmtDate } from '../api';
import PropertyButton from './PropertyButton';

const human = (k) => LABELS[k] || k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
const isPrim = (v) => v == null || ['string', 'number', 'boolean'].includes(typeof v);
const show = (v) => (v === true ? 'Sì' : v === false ? 'No' : v == null || v === '' ? '—' : typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) ? fmtDate(v) : String(v));

export function KV({ data, cols = 2 }) {
  const entries = Object.entries(data || {}).filter(([, v]) => isPrim(v));
  if (!entries.length) return null;
  return (
    <dl className={`grid gap-x-8 gap-y-4 ${cols === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
      {entries.map(([k, v]) => (
        <div key={k} className="border-b border-[#1F242C] pb-3">
          <dt className="text-[11px] uppercase tracking-[0.12em] text-[#5A5F67] mb-1">{human(k)}</dt>
          <dd className="text-sm text-[#E5E5E5] break-words">{show(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Tree({ name, value, depth = 0 }) {
  const [open, setOpen] = useState(depth < 1);
  if (isPrim(value)) {
    return <div className="flex gap-3 py-1 text-xs"><span className="text-[#7A7F87] min-w-[180px]">{human(name)}</span><span className="text-[#E5E5E5] font-mono break-all">{show(value)}</span></div>;
  }
  const entries = Array.isArray(value) ? value.map((v, i) => [`${i + 1}`, v]) : Object.entries(value);
  return (
    <div className="py-1">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs text-[#D4AF37] hover:text-white">
        <ChevronRight size={12} className={`transition-transform ${open ? 'rotate-90' : ''}`} />{human(name)} {Array.isArray(value) && <span className="text-[#5A5F67]">({value.length})</span>}
      </button>
      {open && <div className="pl-4 border-l border-[#1F242C] ml-1.5">{entries.map(([k, v]) => <Tree key={k} name={k} value={v} depth={depth + 1} />)}</div>}
    </div>
  );
}

export function RawData({ report }) {
  return (
    <Card className="p-6" data-testid="report-raw-data">
      <SectionTitle>Tutti i dati del report</SectionTitle>
      {Object.entries(report).filter(([k]) => !['companyId', 'language'].includes(k)).map(([k, v]) => <Tree key={k} name={k} value={v} />)}
    </Card>
  );
}

const SCALE = [['E', '#C2423A'], ['D', '#D9822B'], ['C', '#C9A227'], ['B', '#6FA640'], ['A', '#2E9E6A']];
const RANGES = { E: [0, 0], D: [1, 29], C: [30, 50], B: [51, 70], A: [71, 100] };

// Creditsafe bands are not equal-width: keep the marker inside the segment of the rating.
export function markerPos(band, score) {
  const b = String(band || '').toUpperCase();
  const i = SCALE.findIndex(([x]) => x === b);
  if (i < 0) return Math.min(100, Math.max(0, score));
  const [lo, hi] = RANGES[b];
  const f = hi > lo ? Math.min(1, Math.max(0, (score - lo) / (hi - lo))) : 0.5;
  return ((i + 0.1 + f * 0.8) / 5) * 100;
}

function ScoreScale({ band, score }) {
  return (
    <div className="flex-1 min-w-[220px] max-w-md">
      <div className="relative h-4 mb-1">
        <span className="absolute -translate-x-1/2 text-[#F2F2F2] text-xs leading-none" style={{ left: `${markerPos(band, score)}%` }} data-testid="score-marker">▼</span>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {SCALE.map(([b, c]) => (
          <div key={b} className="h-2.5 rounded-sm" style={{ background: c, opacity: String(band).toUpperCase() === b ? 1 : 0.3 }} />
        ))}
      </div>
      <div className="grid grid-cols-5 gap-1 mt-1 text-[10px] font-mono text-[#5A5F67] text-center">{SCALE.map(([b]) => <span key={b}>{b}</span>)}</div>
    </div>
  );
}

export function RatingSection({ report }) {
  const cs = report.creditScore || {};
  const cur = cs.currentCreditRating || {};
  const prev = cs.previousCreditRating || {};
  const score = Number(cur.providerValue?.value || 0);
  const esg = (report.localIndicators || []).find((i) => i.type === 'ESG');
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="p-6 lg:col-span-2">
        <SectionTitle>Rating e affidamento</SectionTitle>
        <div className="flex items-center gap-6 mb-6">
          <RatingBadge value={cur.commonValue} size="lg" />
          <div className="flex-1 min-w-0">
            <p className="text-lg text-[#F2F2F2] mb-3" data-testid="report-rating-desc">{cur.commonDescription || cur.providerDescription || '—'}</p>
            <div className="flex items-end gap-6 flex-wrap">
              <div data-testid="report-score">
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#5A5F67]">Score</p>
                <p className="font-mono text-[#F2F2F2] leading-none mt-1"><span className="text-4xl">{cur.providerValue?.value ?? '—'}</span><span className="text-sm text-[#7A7F87]"> / {cur.providerValue?.maxValue ?? 100}</span></p>
              </div>
              <ScoreScale band={cur.commonValue} score={score} />
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><p className="text-[11px] uppercase tracking-[0.12em] text-[#5A5F67]">Fido consigliato</p><p className="font-mono text-xl text-[#D4AF37]" data-testid="report-credit-limit">{money(cur.creditLimit?.value, cur.creditLimit?.currency)}</p></div>
          <div><p className="text-[11px] uppercase tracking-[0.12em] text-[#5A5F67]">Rating precedente</p><p className="font-mono text-xl">{prev.commonValue || '—'} · {prev.providerValue?.value ?? '—'}</p></div>
          <div><p className="text-[11px] uppercase tracking-[0.12em] text-[#5A5F67]">Ultima variazione</p><p className="font-mono text-xl">{fmtDate(cs.latestRatingChangeDate)}</p></div>
        </div>
      </Card>
      <Card className="p-6">
        <SectionTitle>ESG risk</SectionTitle>
        {!esg ? <p className="text-sm text-[#5A5F67]">Non disponibile per questo template.</p> : (
          <div className="space-y-3">
            <p className="text-2xl text-[#F2F2F2]">{esg.value?.description}</p>
            {(esg.categories || []).map((c) => (
              <div key={c.type} className="flex justify-between text-sm border-b border-[#1F242C] pb-2"><span className="text-[#8A8F97]">{c.description}</span><span>{c.value?.description}</span></div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export function NegativeSection({ report }) {
  const x = report.companySummaryExtra || {};
  const n = report.negativeInformationExtra || {};
  const flags = [['hasProtesti', 'Protesti'], ['hasSevereProtesti', 'Protesti gravi'], ['hasPrejudicials', 'Pregiudizievoli'], ['hasSeverePrejudicials', 'Pregiudizievoli gravi'], ['hasInsolvency', 'Procedure concorsuali'], ['hasCompaniesInsolvency', 'Insolvenze collegate'], ['hasCigsEvents', 'Eventi CIGS']];
  return (
    <Card className="p-6">
      <SectionTitle>Negatività</SectionTitle>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="report-negative-flags">{flags.map(([k, l]) => <Flag key={k} on={!!x[k]} label={l} />)}</div>
      <KV data={n.prejudicialSummary} />
      {Object.entries(n).filter(([, v]) => Array.isArray(v) && v.length).map(([k, v]) => <Tree key={k} name={k} value={v} />)}
    </Card>
  );
}

const FIN_ROWS = [['profitAndLoss', 'revenue', 'Ricavi'], ['profitAndLoss', 'operatingProfit', 'Risultato operativo'], ['profitAndLoss', 'profitBeforeTax', 'Utile ante imposte'], ['profitAndLoss', 'profitAfterTax', 'Utile netto'], ['balanceSheet', 'totalAssets', 'Totale attivo'], ['balanceSheet', 'totalLiabilities', 'Totale passività'], ['balanceSheet', 'cash', 'Liquidità'], ['otherFinancials', 'netWorth', 'Patrimonio netto'], ['otherFinancials', 'workingCapital', 'Capitale circolante'], ['ratios', 'currentRatio', 'Current ratio'], ['ratios', 'returnOnTotalAssetsEmployed', 'ROA %'], ['ratios', 'gearing', 'Gearing %']];

export function FinancialSection({ report }) {
  const fs = (report.financialStatements || []).slice().sort((a, b) => (a.yearEndDate > b.yearEndDate ? 1 : -1));
  if (!fs.length) return <Card className="p-6"><p className="text-sm text-[#5A5F67]">Bilanci non inclusi in questo template.</p></Card>;
  const chart = fs.map((f) => ({ anno: f.yearEndDate?.slice(0, 4), Ricavi: f.profitAndLoss?.revenue, 'Utile netto': f.profitAndLoss?.profitAfterTax }));
  const tick = (v) => `${Math.round(v / 1000).toLocaleString('it-IT')}k`;
  const mini = (key, color, testid) => (
    <Card className="p-6" data-testid={testid}>
      <SectionTitle>{key}</SectionTitle>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart}>
            <CartesianGrid stroke="#1F242C" vertical={false} />
            <XAxis dataKey="anno" stroke="#5A5F67" fontSize={12} />
            <YAxis stroke="#5A5F67" fontSize={11} tickFormatter={tick} width={56} />
            <Tooltip contentStyle={{ background: '#0B0D10', border: '1px solid #1F242C' }} labelStyle={{ color: '#F2F2F2' }} itemStyle={{ color: '#E5E5E5' }} formatter={(v) => money(v)} cursor={{ fill: 'rgba(212,175,55,0.06)' }} />
            <ReferenceLine y={0} stroke="#5A5F67" />
            <Bar dataKey={key} radius={[2, 2, 0, 0]}>
              {chart.map((c) => <Cell key={c.anno} fill={(c[key] || 0) < 0 ? '#E37A72' : color} data-testid={`${testid}-bar-${c.anno}`} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        {mini('Ricavi', '#D4AF37', 'fin-chart-revenue')}
        {mini('Utile netto', '#5FC18F', 'fin-chart-profit')}
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-financial-table">
          <thead><tr className="border-b border-[#1F242C] text-[11px] uppercase tracking-[0.12em] text-[#5A5F67]"><th className="text-left px-6 py-3 font-normal">Voce</th>{fs.map((f) => <th key={f.yearEndDate} className="text-right px-6 py-3 font-normal">{f.yearEndDate?.slice(0, 4)}</th>)}</tr></thead>
          <tbody className="divide-y divide-[#1F242C]">
            {FIN_ROWS.map(([g, k, l]) => (
              <tr key={k}><td className="px-6 py-3 text-[#A3A3A3]">{l}</td>{fs.map((f) => { const v = f[g]?.[k]; return <td key={f.yearEndDate} className={`px-6 py-3 text-right font-mono ${v != null && Number(v) < 0 ? 'text-[#E37A72]' : ''}`}>{v == null ? '—' : g === 'ratios' ? Number(v).toFixed(2) : money(v, f.currency)}</td>; })}</tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function PeopleSection({ report }) {
  const sh = (report.shareCapitalStructureExtra || {}).shareholders || (report.shareCapitalStructure || {}).shareHolders || [];
  const dirs = (report.directorsExtra || {}).currentDirectors || (report.directors || {}).currentDirectors || [];
  const pct = (s) => (s.shareClasses || []).flatMap((c) => c.additionalData?.quotaValues || []).reduce((a, q) => a + (q.quotaPercentage || 0), 0) || s.percentSharesHeld;
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="p-6">
        <SectionTitle>Soci ({sh.length})</SectionTitle>
        <div className="divide-y divide-[#1F242C]" data-testid="report-shareholders">
          {sh.length === 0 && <p className="text-sm text-[#5A5F67]">Non disponibili per questo template.</p>}
          {sh.map((s, i) => (
            <div key={i} className="py-3 flex justify-between items-center gap-4"><div className="min-w-0 flex-1"><p className="text-sm truncate">{s.name}</p><p className="text-xs text-[#5A5F67] font-mono">{s.id}</p></div><span className="font-mono text-[#D4AF37]">{pct(s) ? `${pct(s)}%` : '—'}</span>
              <PropertyButton compact name={s.name} code={s.id} kind={['Male', 'Female'].includes(s.additionalData?.gender) || s.shareholderType === 'Person' ? 'person' : 'company'} testid={`shareholder-property-btn-${i}`} /></div>
          ))}
        </div>
      </Card>
      <Card className="p-6">
        <SectionTitle>Dirigenti ({dirs.length})</SectionTitle>
        <div className="divide-y divide-[#1F242C]" data-testid="report-directors">
          {dirs.length === 0 && <p className="text-sm text-[#5A5F67]">Non disponibili per questo template.</p>}
          {dirs.map((d, i) => (
            <div key={i} className="py-3 flex items-center gap-4"><div className="min-w-0 flex-1"><p className="text-sm">{d.name}</p><p className="text-xs text-[#8A8F97]">{(d.positions || []).map((p) => p.positionName).join(' · ')}</p><p className="text-xs text-[#5A5F67] font-mono">{d.id} · {fmtDate(d.dateOfBirth)} {d.placeOfBirth || ''}</p></div>
              <PropertyButton compact name={d.name} code={d.id} kind={d.directorType === 'Company' ? 'company' : 'person'} testid={`director-property-btn-${i}`} /></div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export function GroupSection({ report }) {
  const ext = report.extendedGroupStructure || [];
  return (
    <Card className="p-6">
      <SectionTitle>Struttura di gruppo</SectionTitle>
      {ext.length === 0 && !(report.groupStructure?.subsidiaryCompanies || []).length ? <p className="text-sm text-[#5A5F67]">Nessuna struttura di gruppo disponibile.</p> : (
        <div className="space-y-1" data-testid="report-group">
          {(ext.length ? ext : report.groupStructure.subsidiaryCompanies.map((s) => ({ ...s, companyName: s.name, level: 1 }))).map((g, i) => (
            <div key={i} className="flex items-center gap-3 py-2 text-sm" style={{ paddingLeft: `${(g.level || 0) * 24}px` }}>
              <span className="w-1.5 h-1.5 bg-[#D4AF37]" /><span className="flex-1">{g.companyName}</span><span className="text-xs text-[#5A5F67] font-mono">{g.id} · {g.status}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
